import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../services/api';
import { 
  ArrowLeft, Upload, BookOpen, Sparkles, MessageCircle, Trash2, 
  Loader2, X, FileText, Send, ListChecks
} from 'lucide-react';
import { toast } from 'sonner';

const BooksLibrary = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeBook, setActiveBook] = useState(null);
  const fileRef = useRef();

  useEffect(() => { loadBooks(); }, []);

  const loadBooks = async () => {
    try { const res = await apiService.myBooks(); setBooks(res.data.books); }
    catch (e) { toast.error('Failed to load library'); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['pdf', 'epub'].includes(ext)) {
      toast.error('Only PDF and EPUB files supported');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large (max 20MB)');
      return;
    }
    
    const title = file.name.replace(/\.(pdf|epub)$/i, '');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    
    setUploading(true);
    try {
      await apiService.uploadBook(fd);
      toast.success(`"${title}" added to your library!`);
      loadBooks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteBook = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try { await apiService.deleteBook(id); toast.success('Deleted'); loadBooks(); }
    catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-12 h-12 animate-spin text-purple-500" /></div>;

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="books-library-page">
      <div className="bg-gradient-to-r from-amber-600 via-orange-700 to-red-700 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400/20 rounded-full blur-3xl -translate-y-1/2"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/80 hover:text-white font-semibold mb-3"><ArrowLeft className="w-5 h-5" />Dashboard</button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black text-white">My Books Library</h1>
              <p className="text-white/70 text-sm sm:text-base">Upload any book - get summaries, ask questions, generate quizzes</p>
            </div>
            <motion.button onClick={() => fileRef.current?.click()} disabled={uploading} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              data-testid="upload-book-btn"
              className="px-5 py-3 bg-white text-amber-700 rounded-2xl font-black flex items-center gap-2 shadow-xl disabled:opacity-60">
              {uploading ? <><Loader2 className="w-5 h-5 animate-spin" />Uploading...</> : <><Upload className="w-5 h-5" />Add Book</>}
            </motion.button>
            <input ref={fileRef} type="file" accept=".pdf,.epub" onChange={handleUpload} className="hidden" data-testid="book-file-input" />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {books.length === 0 ? (
          <div className="bg-white rounded-3xl p-10 text-center border-2 border-slate-100 shadow-md">
            <BookOpen className="w-20 h-20 text-slate-300 mx-auto mb-3" />
            <h2 className="text-xl font-black text-[#1E293B] mb-2">Your library is empty</h2>
            <p className="text-[#64748B] mb-5 max-w-md mx-auto">Upload a PDF or EPUB to get started. SkillRise will help you understand, summarize, and quiz yourself on it!</p>
            <button onClick={() => fileRef.current?.click()} className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-2xl font-bold flex items-center gap-2 mx-auto">
              <Upload className="w-5 h-5" />Upload Your First Book
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {books.map((b, i) => (
              <motion.div key={b.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl border-2 border-slate-100 shadow-md overflow-hidden hover:shadow-xl transition-all">
                <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-5 text-white relative">
                  <FileText className="w-10 h-10 mb-2 opacity-80" />
                  <h3 className="font-black text-lg leading-tight line-clamp-2" data-testid={`book-title-${b.id}`}>{b.title}</h3>
                  <span className="text-xs uppercase font-bold opacity-70 mt-1 inline-block">{b.file_type}</span>
                </div>
                <div className="p-4 space-y-2">
                  <p className="text-xs text-[#64748B]">{Math.round(b.size_bytes / 1024)} KB · {Math.round(b.text_length / 1000)}k chars</p>
                  <button onClick={() => setActiveBook(b)} data-testid={`open-book-${b.id}`}
                    className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-1.5">
                    <Sparkles className="w-4 h-4" />Open Book
                  </button>
                  <button onClick={() => deleteBook(b.id, b.title)} data-testid={`delete-book-${b.id}`}
                    className="w-full py-2 bg-red-50 text-red-600 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 hover:bg-red-100">
                    <Trash2 className="w-3 h-3" />Delete
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {activeBook && <BookViewer book={activeBook} onClose={() => setActiveBook(null)} />}
      </AnimatePresence>
    </div>
  );
};

const BookViewer = ({ book, onClose }) => {
  const [tab, setTab] = useState('summary');
  const [summary, setSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [quiz, setQuiz] = useState('');
  const [quizLoading, setQuizLoading] = useState(false);

  useEffect(() => { if (tab === 'summary' && !summary) genSummary(); }, [tab]);

  const genSummary = async () => {
    setSummaryLoading(true);
    try { const r = await apiService.bookSummary(book.id); setSummary(r.data.summary); }
    catch (e) { toast.error('Summary failed'); }
    finally { setSummaryLoading(false); }
  };

  const askQuestion = async () => {
    if (!question.trim()) return;
    const q = question;
    setQuestion('');
    setConversation(prev => [...prev, { role: 'user', text: q }]);
    setAskLoading(true);
    try {
      const r = await apiService.askBook(book.id, { question: q });
      setConversation(prev => [...prev, { role: 'tutor', text: r.data.answer }]);
    } catch (e) { toast.error('Q&A failed'); }
    finally { setAskLoading(false); }
  };

  const genQuiz = async () => {
    setQuizLoading(true);
    try { const r = await apiService.bookQuiz(book.id); setQuiz(r.data.quiz); }
    catch (e) { toast.error('Quiz failed'); }
    finally { setQuizLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <motion.div initial={{ y: 200 }} animate={{ y: 0 }} exit={{ y: 200 }}
        className="bg-white rounded-t-3xl sm:rounded-3xl max-w-3xl w-full max-h-[95vh] sm:max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
        
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <BookOpen className="w-6 h-6 text-white flex-shrink-0" />
            <h2 className="text-lg font-black text-white truncate">{book.title}</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex bg-slate-50 border-b">
          {[
            { id: 'summary', icon: <Sparkles className="w-4 h-4" />, label: 'Summary' },
            { id: 'ask', icon: <MessageCircle className="w-4 h-4" />, label: 'Ask AI' },
            { id: 'quiz', icon: <ListChecks className="w-4 h-4" />, label: 'Quiz' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`book-tab-${t.id}`}
              className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-1.5 transition ${tab === t.id ? 'bg-white text-amber-600 border-b-2 border-amber-500' : 'text-slate-500'}`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'summary' && (
            <div>
              {summaryLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-10 h-10 animate-spin text-amber-500 mb-3" />
                  <p className="font-bold text-slate-600">Reading your book...</p>
                </div>
              ) : (
                <div className="prose max-w-none whitespace-pre-wrap text-[#1E293B] leading-relaxed text-sm">{summary || 'Generating summary...'}</div>
              )}
            </div>
          )}

          {tab === 'ask' && (
            <div className="flex flex-col h-full min-h-[300px]">
              <div className="flex-1 space-y-3 mb-3 overflow-y-auto">
                {conversation.length === 0 ? (
                  <div className="text-center py-10">
                    <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">Ask anything about this book!</p>
                    <p className="text-slate-400 text-xs mt-1 italic">E.g. "Who is the main character?" or "Explain chapter 2"</p>
                  </div>
                ) : (
                  conversation.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                        m.role === 'user' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-[#1E293B]'
                      }`}>{m.text}</div>
                    </div>
                  ))
                )}
                {askLoading && (
                  <div className="flex justify-start"><div className="bg-slate-100 px-3 py-2 rounded-2xl"><Loader2 className="w-4 h-4 animate-spin text-amber-500" /></div></div>
                )}
              </div>
              <div className="flex gap-2 mt-auto">
                <input type="text" value={question} onChange={e => setQuestion(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && askQuestion()}
                  placeholder="Ask anything about this book..."
                  className="flex-1 p-3 bg-slate-50 border-2 border-slate-200 rounded-xl focus:border-amber-500 outline-none font-medium text-sm"
                  data-testid="book-question-input" />
                <button onClick={askQuestion} disabled={askLoading || !question.trim()} data-testid="book-ask-btn"
                  className="px-5 bg-amber-500 text-white rounded-xl font-bold disabled:opacity-50">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {tab === 'quiz' && (
            <div>
              {!quiz ? (
                <div className="text-center py-10">
                  <ListChecks className="w-16 h-16 text-amber-300 mx-auto mb-3" />
                  <p className="font-bold text-slate-600 mb-4">Generate a 10-question quiz from this book</p>
                  <button onClick={genQuiz} disabled={quizLoading} data-testid="generate-quiz-btn"
                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-2xl font-bold disabled:opacity-50 flex items-center gap-2 mx-auto">
                    {quizLoading ? <><Loader2 className="w-5 h-5 animate-spin" />Generating...</> : <><Sparkles className="w-5 h-5" />Generate Quiz</>}
                  </button>
                </div>
              ) : (
                <div className="whitespace-pre-wrap text-[#1E293B] text-sm leading-relaxed bg-amber-50 rounded-2xl p-5 border-2 border-amber-200" data-testid="quiz-content">{quiz}</div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BooksLibrary;
